/* eslint-disable no-console, no-unused-vars */
/**
 * Log levels
 */
enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
}

/**
 * Centralized Logger Service
 */
class Logger {
  private static instance: Logger;
  private level: LogLevel;

  private constructor() {
    this.level = process.env.NODE_ENV === 'development' ? LogLevel.DEBUG : LogLevel.WARN;
  }

  /**
   * Get the single logger instance
   */
  public static getInstance(): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger();
    }
    return Logger.instance;
  }

  /**
   * Format log message with timestamp
   */
  private formatMessage(level: string, message: string) {
    const timestamp = new Date().toISOString();
    return `[${timestamp}] [${level}] ${message}`;
  }

  /**
   * Debug logs (development only)
   */
  public debug(message: string, context?: unknown) {
    if (this.level <= LogLevel.DEBUG) {
      console.log(this.formatMessage('DEBUG', message), context ?? '');
    }
  }

  /**
   * Informational logs
   */
  public info(message: string, context?: unknown) {
    if (this.level <= LogLevel.INFO) {
      console.info(this.formatMessage('INFO', message), context ?? '');
    }
  }

  /**
   * Warning logs
   */
  public warn(message: string, context?: unknown) {
    if (this.level <= LogLevel.WARN) {
      console.warn(this.formatMessage('WARN', message), context ?? '');
    }
  }

  /**
   * Error logs
   */
  public error(message: string, error?: unknown, context?: unknown) {
    if (this.level <= LogLevel.ERROR) {
      console.error(this.formatMessage('ERROR', message), {
        error,
        context,
      });

      // Future monitoring integrations
      // Example:
      // Sentry.captureException(error);
    }
  }
}

export const logger = Logger.getInstance();
